import { Link } from 'react-router-dom'
import MetaTags from '../components/MetaTags'
import DinBilpartnerBadge from '../components/DinBilpartnerBadge'
import ReviewsSlider from '../components/ReviewsSlider'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'

export default function DaekskiftPage({ settings }) {
  const bookingUrl = settings?.booking_url || 'https://service.dinbilpartner.dk/book-tid'
  const phone = settings?.phone || '22 18 73 30'
  const phoneRaw = settings?.phone_raw || '22187330'

  return (
    <>
      <MetaTags
        title="Dækskift | Udskiftning af dæk nær Billund og Grindsted - EK Biler"
        description="Hos EK Biler tilbyder vi dækskift på vores autoværksted. Få skiftet dine dæk Hejnsvig nær Billund og Grindsted. Book tid her"
        ogTitle="Dækskift | Udskiftning af dæk nær Billund og Grindsted - EK Biler"
        ogDescription="Hos EK Biler tilbyder vi dækskift på vores autoværksted. Få skiftet dine dæk Hejnsvig nær Billund og Grindsted. Book tid her"
        ogImage={imageUrl('shutterstock-2416863723-optimized-1.jpg', 'width=1200,quality=85,format=auto')}
      />

      {/* Hero Header */}
      <section className="bg-[#14171a] text-white py-14 sm:py-20 border-b border-[#262c33] relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src={imageUrl('shutterstock-2416863723-optimized-1.jpg', 'width=1600,quality=80,format=auto')}
            alt="Dækskift på autoværksted"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl space-y-4">
            <div className="flex items-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-wider">
              <Link to="/" className="hover:text-white">Forside</Link>
              <span>/</span>
              <span className="text-[#e20019]">Autoværksted</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white">
              Dækskift
            </h1>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
              Professionel service og reparation og skader af alle bilmærker. Speciale i V.A.G
              Gruppen og altid mulighed for lånebil, når din bil er på værksted.
            </p>
            <div className="pt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <a
                href={bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-red-500 text-center"
              >
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                <span>Book tid til dækskift</span>
              </a>
              <a
                href={`tel:${phoneRaw}`}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#20252c] hover:bg-[#2b323c] border border-gray-700 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors whitespace-nowrap text-center"
              >
                <i className="fa-solid fa-phone text-[#e20019]" aria-hidden="true" />
                <span>Ring på {phone}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content from Scrape */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            {/* Left Column */}
            <div className="lg:col-span-7 space-y-8 text-gray-800 leading-relaxed text-base">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
                  Dækskift hos EK Biler
                </h2>
                <p>
                  Hos EK Biler varetager vores autoværksted dækskift som en del af vores ydelser. Vi
                  servicerer bilejere fra Billund, Grindsted og omegn – og vores dygtige mekanikere står
                  klar til at hjælpe med både sommerdæk og vinterdæk. Hvad enten du har brug for en
                  hurtig udskiftning af dæk eller rådgivning om, hvilke dæk der passer bedst til din bil,
                  kan du regne med os. Vi sørger for, at din bil altid er klædt på til vejene – uanset
                  årstid og vejrforhold.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Udskiftning af dæk på alle biler
                </h2>
                <p>
                  Elbiler stiller ofte særlige krav til dækkene på grund af deres vægt og moment. Som
                  erfarent autoværksted nær Billund og Grindsted kan vi håndtere dækskift på alle biler.
                  Det betyder, at du kan være sikker på optimal sikkerhed og komfort – hvad end du kører
                  elbil eller en benzinbil. Vores dygtige mekanikere arbejder med de rette værktøjer,
                  lifte og metoder for at beskytte både dæk og bil under hele processen. Vi kan også
                  hjælpe dig med{' '}
                  <Link to="/elbil-service" className="text-[#e20019] font-bold hover:underline">
                    elbil service
                  </Link>{' '}
                  så din bil for optimal drift og længere levetid.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Hvorfor skal man have foretaget dækskift?
                </h2>
                <p>
                  Udskiftning af dæk er afgørende for at sikre din egen og andres sikkerhed på vejene.
                  Slidte dæk eller dæk, der ikke passer til sæsonen, kan nemlig have alvorlige
                  konsekvenser – herunder:
                </p>
                <div className="space-y-4 mt-4">
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Reduceret vejgreb:</strong>
                    <span className="text-sm text-gray-600">
                      Slidte dæk eller forkert dæktype kan forringe vejgrebet – især på våde, isglatte
                      eller sneklædte veje.
                    </span>
                  </div>
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Forlænget bremselængde:</strong>
                    <span className="text-sm text-gray-600">
                      Dårlige dæk kan øge bremselængden markant, hvilket kan have alvorlige
                      konsekvenser i farlige situationer.
                    </span>
                  </div>
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Øget slid på bilen:</strong>
                    <span className="text-sm text-gray-600">
                      Hvis dine dæk ikke passer til forholdene, kan det også belaste bilens andre
                      komponenter unødigt, f.eks. affjedringen og styretøjet.
                    </span>
                  </div>
                  <div className="border-l-4 border-[#e20019] pl-4">
                    <strong className="block text-gray-900">Dårligere brændstoføkonomi:</strong>
                    <span className="text-sm text-gray-600">
                      Forkert dæktryk eller dæktype kan medføre et højere brændstofforbrug. Dette vil i
                      sidste ende koste dig penge.
                    </span>
                  </div>
                </div>
                <p className="mt-4 font-semibold text-gray-900 text-sm">
                  Med dækskift undgår du disse problemer. De rette dæk er med andre ord en forudsætning
                  for sikker (og mere økonomisk) kørsel!
                </p>
              </div>

              {/* Photo break */}
              <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                <img
                  src={imageUrl('shutterstock-2416863723-optimized-1.jpg', 'width=800,quality=80,format=auto')}
                  alt="dækskifte på autoværksted"
                  className="w-full h-auto object-cover"
                />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Følg sæsonen – vi rådgiver</h3>
                <p>
                  Timingen er afgørende, når det kommer til dækskift. Vinterdæk bør monteres, når
                  temperaturen begynder at falde til omkring 7 grader, typisk i oktober eller november.
                  Sommerdæk bør omvendt monteres, når temperaturen stabilt ligger over 7 grader, ofte i
                  marts eller april.
                </p>
                <p className="mt-2 text-sm text-gray-700">
                  Hos EK Biler har vi et praktisk{' '}
                  <Link to="/daekhotel" className="text-[#e20019] font-bold hover:underline">
                    dækhotel
                  </Link>
                  , hvor du kan opbevare dine dæk sikkert og korrekt, når de ikke er i brug. På den måde
                  slipper du for besværet med opbevaring derhjemme – og vi sikrer, at dine dæk er klar,
                  når det er tid til det næste dækskift.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Tid til dækskift? Kontakt os!</h3>
                <p>
                  Skal du have foretaget udskiftning af dæk? Uanset om du bor i Billund, Grindsted eller
                  et andet sted i nærområdet, er vi klar til at hjælpe hos EK Biler. Kontakt os i dag og
                  book en tid – så sørger vi for, at din bil er i topform til både sommer- og
                  vinterkørsel.
                </p>
              </div>
            </div>

            {/* Right Column: Sticky Booking Card & Contact Form */}
            <div className="lg:col-span-5 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#14171a] text-white p-6 sm:p-8 rounded-2xl border border-gray-800 space-y-4">
                <div className="text-xs font-bold text-[#e20019] uppercase tracking-wider">
                  Dækskifte på værkstedet
                </div>
                <h3 className="text-2xl font-bold">Bestil tid til dækskift</h3>
                <p className="text-sm text-gray-300">
                  Hurtigt og professionelt dækskift på vores autoværksted i Hejnsvig.
                </p>
                <div className="pt-2">
                  <a
                    href={bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full text-center inline-flex items-center justify-center gap-2 bg-[#e20019] hover:bg-[#b80014] text-white py-3.5 px-6 rounded-xl font-bold text-sm shadow-md transition-all whitespace-nowrap"
                  >
                    <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                    <span>Book tid til dækskift</span>
                  </a>
                </div>
              </div>

              <ContactForm
                title="Send en forespørgsel"
                subtitle="Vi vender tilbage for at fuldføre din booking"
                bookingUrl={bookingUrl}
              />
            </div>
          </div>
        </div>
      </section>

      <DinBilpartnerBadge />
      <ReviewsSlider />
    </>
  )
}
